import { useEffect, useRef, useState } from 'react';

const FeatureSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const currentRef = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.2, // Trigger when 20% of the section is visible
      }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-8 sm:py-12 md:py-16 lg:py-24 px-2 sm:px-4 md:px-6 bg-white overflow-hidden">
      <div className="max-w-screen-2xl mx-auto">
          <div className="text-center mb-6 sm:mb-10 md:mb-12 lg:mb-16">
            <h2 className="text-xl sm:text-2xl md:text-4xl lg:text-6xl font-serif font-bold text-gray-900 mb-3 sm:mb-4 md:mb-6">
              Making every picture <span className="bg-[#E85D44] text-[#FFD93D] px-2 sm:px-4 md:px-6 py-0.5 sm:py-1 md:py-2 inline-block">powerful</span>
            </h2>
          </div>


          <div className="relative mb-6 sm:mb-8 md:mb-10 lg:mb-12 max-w-5xl mx-auto">
            <div className="grid grid-cols-2 gap-2 sm:gap-4 md:gap-6 lg:gap-8">

              {/* Column 1: Original Photo - Slide in from left with color box overlay */}
              <div
                className={`relative transition-all duration-1500 ease-out ${
                  isVisible
                    ? 'opacity-100 translate-x-0'
                    : 'opacity-0 -translate-x-20'
                }`}
              >
                <div className="relative bg-white rounded-lg sm:rounded-xl md:rounded-2xl p-2 sm:p-3 md:p-4 lg:p-6 shadow-md sm:shadow-lg md:shadow-xl overflow-hidden hover:shadow-xl sm:hover:shadow-2xl transition-shadow duration-300">
                  {/* Color box overlay that slides away */}
                  <div
                    className={`absolute inset-0 bg-[#E85D44] z-10 transition-transform duration-1500 ease-in-out ${
                      isVisible ? '-translate-x-full' : 'translate-x-0'
                    }`}
                    style={{ transitionDelay: '1000ms' }}
                  ></div>
                  <img
                    src="/images/hero-03-collage.png"
                    alt="Your original"
                    className="w-full h-auto rounded-md sm:rounded-lg md:rounded-xl relative z-0"
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              </div>

              {/* Column 2: Lithophane Lamp - Fade in smoothly */}
              <div
                className={`relative flex items-center transition-all duration-1500 ease-out ${
                  isVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: '1200ms' }}
              >
                <div className="w-full">
                  <div className="bg-white rounded-lg sm:rounded-xl md:rounded-2xl p-2 sm:p-3 md:p-4 lg:p-6 shadow-md sm:shadow-lg md:shadow-xl hover:shadow-xl sm:hover:shadow-2xl transition-shadow duration-300">
                    <img
                      src="/images/hero-01-on.png"
                      alt="3D Lithophane Night Lamp"
                      className="w-full h-auto rounded-md sm:rounded-lg md:rounded-xl"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Curved Arrow Overlay - Draw animation using SVG path */}
            <div
              className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none hidden md:block transition-all duration-1000 ease-out ${
                isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
              }`}
              style={{ width: '220px', zIndex: 10, transitionDelay: '1000ms' }}
              
            >
              <svg
                 viewBox="0 0 1000 378"
                className="w-full h-auto"
                xmlns="http://www.w3.org/2000/svg"
                
              >
                {/* Arrow path with draw animation */}
                <path
                  d="M365.8 97.7c6.1.2 16.3.2 22.5 0 6.1-.1 1.1-.3-11.3-.3s-17.4.2-11.2.3"/><path d="M415.5 26.7c-2.7.2-12.6.8-22 1.3-22.7 1.3-35.8 2.5-48.5 4.5-5.8.9-13.6 2-17.5 2.5-9.4 1.3-15.6 2.5-24 4.5-3.8 1-8.7 2-10.8 2.2-2 .3-4 .9-4.3 1.4s-1.6.9-2.9.9-3.4.4-4.7.9c-2.1.9-6.2 2.1-18.3 5.3-5.2 1.4-11.4 3.4-14.5 4.6-1.4.5-4.5 1.5-7 2.2s-5.4 1.6-6.5 2c-1.1.5-4.2 1.6-7 2.5-2.7 1-5.9 2.1-7 2.5-1.1.5-3.9 1.5-6.2 2.4-4.5 1.7-10.7 4.3-19.1 8.2-2.9 1.3-5.7 2.4-6.3 2.4-1.4 0-35 16.8-36.9 18.4-.8.8-3.3 2.2-5.5 3.2s-6.1 3.3-8.7 5.1-5.1 3.3-5.4 3.3-2 1-3.7 2.2c-1.7 1.3-3.9 2.7-4.9 3.3-12.6 7.4-33.1 25-51.8 44.3-16.3 16.9-19.9 21-24.6 28.1l-4.4 6.6c-4 5.9-7 10.5-7 11 0 .2-1.5 2.8-3.3 5.7-3.7 5.9-8.8 16.2-11 22.3-4.5 12.1-6.6 18.6-7.7 23.5-.7 3-1.7 6.8-2.2 8.5-2.8 8.6-4.6 32.4-2.8 36.4 3.5 7.7 3.4 7.6 7.5 7.5 6.7-.3 9.8-1.5 13.8-5.3 4.8-4.6 6.7-8.4 6.7-13.5 0-4 1.5-9.3 5.1-17.3 1.1-2.4 1.9-4.8 1.9-5.4s.6-2.3 1.4-3.7c.7-1.5 1.8-3.8 2.4-5.2 2.4-5.6 8.4-17.7 10.2-20.5 1.1-1.7 2.6-4.4 3.3-6 .8-1.7 2.1-3.6 3-4.4 1-.7 1.7-1.8 1.7-2.3 0-1.8 5.1-7.6 20-23 13.4-13.7 24.7-23.5 38.7-33.1 5.4-3.7 22-13.7 22.7-13.7.3 0 3.7-1.9 7.8-4.3 4-2.4 8.9-4.9 10.8-5.7 1.9-.7 7.3-3.1 12-5.2 4.7-2.2 9.4-4.3 10.5-4.8 1.1-.4 4-1.7 6.5-2.8s11.7-4.3 20.5-7.2 16.8-5.6 17.7-6.1c1-.5 3-.9 4.5-.9 1.6-.1 3.9-.7 5.3-1.5s3.9-1.4 5.6-1.5c1.6 0 3.4-.4 4-.9.5-.5 2.7-1.1 4.9-1.4 5.3-.8 12.2-2.1 22-4.2 9-1.9 17.9-3.2 30.6-4.4 4.8-.4 9.1-1.1 9.7-1.5 2.1-1.3 34.3-3 57.2-3 25.5 0 46.3.9 57.8 2.5 4.1.6 11.7 1.5 17 2 24.2 2.4 27.9 2.8 30.2 3.3 1.4.3 8.6 1.5 16 2.7 23.2 3.7 30 5.1 40.2 7.8 4.3 1.2 8.9 2.1 10.3 2.1s3.3.4 4.3.9c1.5.8 4.8 1.7 18.7 5.3 2.2.5 4.8 1.4 5.7 1.9 1 .5 2.3.9 3 .9s5.6 1.4 10.8 3.1 10.9 3.5 12.5 3.9c3.6 1 13.8 4.7 18 6.5 1.7.7 4.8 1.8 7 2.5s5.9 2.1 8.2 3.2c2.4 1 4.6 1.8 5.1 1.8.9 0 16.5 6.5 20.1 8.4 4.1 2.2 15.9 7.6 16.6 7.6 1.2 0 14 6.4 14.3 7.2.2.4.9.8 1.6.8 1.4 0 24 11.5 27.8 14.1 1.4 1.1 2.8 1.9 3.1 1.9s4 2 8.1 4.5c4.2 2.5 7.9 4.5 8.1 4.5.3 0 2.8 1.6 5.5 3.5s5.3 3.5 5.7 3.5c.5.1 4.2 2.4 8.4 5.3s11.1 7.6 15.5 10.6c4.3 2.9 11.3 7.9 15.4 11s8.6 6.4 10 7.4c3.4 2.4 7.9 5.8 20.1 15.1 23.6 18.1 24.9 19.1 30.7 22.7 1.7 1.2 3.2 2.5 3.2 3.1 0 1.1-11.9.6-22.5-1-3.3-.5-13.9-1.9-23.5-3.2-9.6-1.2-18.4-2.6-19.5-3.1-1.1-.4-4.5-1-7.5-1.3-16-1.3-26.1-2.5-27.1-3.2-1.6-.9-20.5-2.6-29.9-2.6-20.9-.1-36 6.9-36 16.6 0 1.5-.5 3.7-1.1 4.8-.7 1.4-.8 2.8-.1 4.2.5 1.1 1.3 3.4 1.8 5.1.8 2.4 2.5 3.8 9.1 7.1 4.5 2.3 10.3 5 13 6 2.6.9 5.3 2 5.8 2.4.6.4 4.6 1.9 9 3.4s8.9 3.1 10 3.5c4.3 1.7 9.2 3.1 10.8 3.1.9 0 3.1.6 4.9 1.4 4 1.7 7.7 2.7 22.8 6.1 14.1 3.3 18.6 4.4 22.6 5.6 1.7.5 4.3.9 5.8.9s3.7.5 4.9 1.2c1.2.6 4.2 1.5 6.7 1.9l9 1.8c19 4.1 21.1 4.5 42.5 7.1 9.9 1.2 30 2.7 47 3.6 12.9.7 22-.8 36.5-5.7 8.2-2.8 10.1-3.9 14.3-8.3 5.2-5.4 11.7-14.5 11.7-16.5 0-.6.5-1.1 1-1.1.6 0 1-.7 1-1.6 0-.8.9-3.6 2.1-6.1 2.5-5.5 3.7-11.5 4.9-25.1.5-5.7 1.3-10.9 1.7-11.5.8-1.2 1.4-12.1 3.3-65.7 1.7-43.9 1.7-66.9.2-78-.7-5.2-1.6-12.4-2-16s-1.2-7.2-1.7-8-1.3-4.7-1.7-8.5c-1.4-12.7-4.3-27.6-6.9-34.9-1.5-4.3-6.3-7.9-8.9-6.6-.8.4-3.4 1-5.7 1.4-6.8 1-16 8.1-19.5 15.1-2 3.8-3.8 13.5-3.9 20.6 0 3-.5 7.9-1 10.9-1.2 7.2-3.2 45.2-3.1 60.5 0 14.3 1.5 41 3.2 55.5 1.1 9.9.8 17-.8 17-1 0-15.2-14.3-15.2-15.4 0-.4-1.8-2.9-4-5.4-2.2-2.6-6.2-7.3-8.9-10.5-5-5.9-16.1-16.3-27.5-25.7-3.7-3-8.6-7.2-11-9.3-2.4-2-4.8-3.7-5.3-3.7-.6 0-1.7-.7-2.4-1.7-.8-.9-5-4-9.4-7-4.4-2.9-8.9-5.9-10-6.7-2.2-1.5-19.1-12.5-21.6-14.1-1-.5-4.1-2.3-7-3.8-3-1.6-6.3-3.7-7.5-4.8-1.1-1-2.4-1.9-3-1.9-.5 0-6.5-3.2-13.4-7.1-6.9-3.8-19.7-10.5-28.5-14.8-16.8-8.3-19-9.3-35-16.1-5.5-2.3-12.2-5.3-15-6.5-2.7-1.2-7.7-3.2-11-4.5-3.3-1.2-8.2-3.1-10.9-4.2s-7.4-2.8-10.5-3.8c-3.1-1.1-10.8-3.8-17.1-6-11.7-4.2-16.6-5.7-21-6.6-1.4-.3-3.4-1-4.5-1.4-1.1-.5-6.3-2.1-11.5-3.5-5.2-1.3-10.8-2.9-12.5-3.4-1.6-.5-5-1.3-7.5-1.9-14.5-3.4-18.1-4.1-23.5-5.2-3.3-.6-8.9-1.7-12.5-2.5-3.6-.7-9.4-1.9-13-2.5s-7.4-1.4-8.5-1.6c-1.1-.3-4.9-.9-8.5-1.4-42-5.2-53.2-6.3-70-7.1-26.8-1.2-50.5-1.7-56.5-1.2"
                  fill="#E85D44"
                  stroke="#E85D44"
                  strokeWidth="10"
                  strokeLinecap="round"
                  strokeDashoffset={isVisible ? 0 : 250}
                  style={{
                    transition: 'stroke-dashoffset 2s ease-out',
                    transitionDelay: '1200ms'
                  }}
                />
                {/* Arrowhead - fades in after path draws */}
                
              </svg>
            </div>
          </div>


          <div className="text-center px-2 sm:px-4">
            <h3 className={`text-sm sm:text-lg md:text-2xl lg:text-3xl xl:text-4xl font-serif font-bold text-gray-900 uppercase leading-tight transition-all duration-1500 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '2000ms' }}
            >
              TURN YOUR PHOTOS TO A CUSTOM
              <br />
              <span className="text-base sm:text-xl md:text-3xl lg:text-4xl xl:text-5xl text-[#E85D44]">ONE OF A KIND 3D NIGHT LAMP</span>
            </h3>
          </div>
        </div>
      </section>
  );
};

export default FeatureSection;
